import type { InternalPage } from './pages.ts'

/** A keypress, narrowed to the fields the page-search shortcut reads. */
export interface PageSearchKey {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
  repeat: boolean
}

/**
 * Whether this platform uses the command key for page search.
 *
 * @param platform - `navigator.platform`, or a stand-in in a test.
 * @returns True on macOS and on iOS.
 */
export function isApplePlatform(
  platform: string = navigator.platform
): boolean {
  return /Mac|iPhone|iPad|iPod/.test(platform)
}

/**
 * The chord the search control shows.
 *
 * @param apple - Whether the reader is on an Apple platform.
 * @returns `⌘K` there, and `Ctrl+K` everywhere else.
 */
export function searchShortcutLabel(apple: boolean): string {
  return apple ? '⌘K' : 'Ctrl+K'
}

/**
 * Whether this keypress opens or closes page search.
 *
 * ⌘K on an Apple platform, Ctrl+K elsewhere. The other modifier does not
 * count, so that platform's own shortcut still runs.
 *
 * @param event - The keypress.
 * @param apple - Whether the reader is on an Apple platform.
 * @returns True when the chord matches.
 */
export function isPageSearchShortcut(
  event: PageSearchKey,
  apple: boolean
): boolean {
  if (event.repeat || event.altKey || event.shiftKey) return false
  if (event.key.toLowerCase() !== 'k') return false
  return apple
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey
}

/**
 * The text page search matches for one page.
 *
 * The rail group is included even when it differs from the page's own group,
 * so a search for the section the reader sees still finds the page. The id
 * keeps the value unique, which cmdk requires when two rows could share a name.
 *
 * @param page - The declared page.
 * @param railGroup - The section name the rail shows for this page.
 * @returns The title, the groups, the description, and the id.
 */
export function pageSearchValue(page: InternalPage, railGroup: string): string {
  const parts = [page.title, railGroup]
  if (page.group && page.group !== railGroup) parts.push(page.group)
  parts.push(page.description, page.id)
  return parts.join(' ')
}

/**
 * How well one page's search text matches what the reader typed.
 *
 * A match is the typed text as one unbroken run anywhere in the title, the
 * groups, the description or the id, ignoring case. A loose run of letters
 * spread across the text is not a match: long descriptions share those, and
 * the list would not narrow.
 *
 * @param value - The text `pageSearchValue` built for one page.
 * @param search - What the reader typed.
 * @returns 1 when the text contains the query, and 0 when it does not.
 */
export function pageSearchScore(value: string, search: string): number {
  const query = search.trim().toLowerCase()
  if (query === '') return 1
  return value.toLowerCase().includes(query) ? 1 : 0
}
